import { StencilCDNImage } from '@/components/Common/StencilImage'
import { useRouter } from 'next/router'
import { generateDetailUrl } from '../../Listing/constants'
import { TINStocksSummaryHoldingData } from '../../types/TINStocksSummaryHoldingsData'
import HoldingBottomComponent from '../HoldingBottomComponent'
import HoldingCard from './HoldingCard'
import { useMemo, useState } from 'react'
import useGetINStockLivePriceFromSocket from '../../LivePrices/useGetINStockLivePriceFromSocket'
import classNames from 'classnames'
import { TLivePrice } from '../../Pages/INStockDetailPage/types'
import MyHoldingTickers from './MyHoldingTickers'
import useGetINStockLivePriceFromMultipleSockets from '../../LivePrices/useGetINStockLivePriceFromMultipleSockets'
import { formatNumberToINRCommaSeparatedString } from '@/root/utils/common/numbers/format-number-to-string'

export function LogoComp({
  logoUrl,
  name,
  width = 50,
  height = 50,
}: {
  logoUrl?: string
  name?: string
  width?: number
  height?: number
}) {
  return (
    <div className="items-center pr-2 md:flex">
      {logoUrl ? (
        <StencilCDNImage
          src={logoUrl}
          width={width}
          height={height}
          className="rounded-full"
        />
      ) : (
        <div className="bg-brand-grey-light flex h-12 w-12 items-center justify-center rounded-full text-xs">
          {name?.slice(0, 4).toUpperCase()}
        </div>
      )}
    </div>
  )
}

export function NameCompWithPrice({
  name,
  live_price,
  day_change_percentage,
  socket_path,
  nameSizeBase = false,
}: {
  name?: string
  live_price?: number
  day_change_percentage?: number
  socket_path?: string
  nameSizeBase?: boolean
}) {
  const [livePrice, setLivePrice] = useState<TLivePrice | null>(null)

  useGetINStockLivePriceFromSocket(socket_path, setLivePrice)

  const livePriceNumber = livePrice?.price ?? live_price

  const dayChangePerc = livePrice?.percent_change ?? day_change_percentage

  return (
    <div
      className={classNames(
        'hover:text-brand-blue-azureRadiance flex cursor-pointer flex-col justify-center  text-lg font-medium',
        { '!text-base': nameSizeBase },
      )}
    >
      <span className="overflow-hidden text-ellipsis whitespace-pre-wrap">
        {name}
      </span>
      {typeof livePriceNumber === 'number' &&
        typeof dayChangePerc === 'number' && (
          <span className="whitespace-prewrap overflow-hidden text-ellipsis text-left text-sm">
            <span className="text-brand-grey-base mr-2">
              {'Price: '}₹{livePriceNumber.toFixed(2)}{' '}
            </span>
            <span
              className={`${
                dayChangePerc >= 0
                  ? 'text-brand-green-dark'
                  : 'text-brand-red-cinnabar'
              } inline-flex items-center`}
            >
              {getChangeUpOrDownArrow(dayChangePerc)}
              {dayChangePerc.toFixed(2)}%
            </span>
          </span>
        )}
    </div>
  )
}

export function QtyComp({
  qty,
  average_price,
}: {
  qty: number
  average_price?: number
}) {
  return (
    <div className="flex flex-col items-center gap-1 lg:flex-row">
      {average_price && (
        <span className="text-brand-grey-base flex items-center">
          <span className="text-sm">
            {formatNumberToINRCommaSeparatedString(average_price)}
            <span className="hidden lg:inline">{' |'}</span>
          </span>
        </span>
      )}

      <span className="text-brand-grey-base flex items-center">
        <span className="text-xs">{qty > 0 ? '+' : ''}</span>
        <span className="text-sm">{qty} Qty</span>
      </span>
    </div>
  )
}

export function getChangeUpOrDownArrow(num: number): JSX.Element | null {
  const changeIsPositive = num && num >= 0
  const changeIsNegative = num && num < 0

  let changeUpOrDownArrow: JSX.Element | null = null

  if (changeIsPositive) {
    changeUpOrDownArrow = <span className="mr-1">&#9650;</span>
  }

  if (changeIsNegative) {
    changeUpOrDownArrow = (
      <span className="mr-1 inline-block rotate-180">&#9650;</span>
    )
  }
  return changeUpOrDownArrow
}

export default function MyHoldings({
  summaryData,
}: {
  summaryData: TINStocksSummaryHoldingData
}) {
  const router = useRouter()

  const holdings =
    summaryData.demat_summary?.asset_summary?.scrip_details?.filter(
      item => item.holdings,
    )

  const summary_for_tickers =
    summaryData.demat_summary?.asset_summary?.scrip_details?.filter(
      item => item.aggregated,
    )

  const [livePriceArr, setLivePriceArr] = useState<
    Record<
      string,
      { price: number | undefined; percChange: number | undefined } | undefined
    >
  >({})

  const socketPathArr =
    useMemo(
      () => summary_for_tickers?.map(item => item.metadata?.socket_path),
      [summary_for_tickers],
    ) ?? []

  useGetINStockLivePriceFromMultipleSockets(socketPathArr, setLivePriceArr)

  return (
    <div className="mt-6 flex flex-col gap-4">
      {!!holdings?.length && (
        <MyHoldingTickers
          summary_for_tickers={summary_for_tickers}
          livePriceArr={livePriceArr}
        />
      )}

      {!!holdings?.length &&
        holdings.map((item, idx) => {
          if (!item.holdings) return null

          const livePrice =
            livePriceArr[item.metadata?.socket_path ?? '']?.price ??
            item.metadata?.live_price ??
            0

          const avg_price = item.holdings.avg_price ?? 0

          const dayChangePerc =
            livePriceArr[item.metadata?.socket_path ?? '']?.percChange ??
            item.metadata?.day_change_percentage ??
            0

          const quantity = item.holdings.quantity ?? 0
          const prevClose = item.metadata?.previous_close ?? 0

          const currValue = livePrice * quantity

          const overall_pnl_live =
            currValue - (item.holdings.invested_amount ?? 0)

          const daypnlLive = (livePrice - prevClose) * quantity

          const overall_pnl_perc_live =
            ((livePrice - avg_price) / avg_price) * 100
          const day_pnl_perc_live = ((livePrice - prevClose) / prevClose) * 100

          const isExternalStock =
            !item.metadata?.name && item.metadata?.live_price === 0

          return (
            <HoldingCard
              key={idx}
              logoComp={
                <LogoComp
                  logoUrl={item.metadata?.logo}
                  name={item.metadata?.name}
                  width={50}
                  height={50}
                />
              }
              nameComp={
                <NameCompWithPrice
                  name={item.metadata?.name}
                  live_price={isExternalStock ? undefined : livePrice}
                  day_change_percentage={dayChangePerc}
                />
              }
              qtyComp={
                typeof item.holdings.quantity === 'number' ? (
                  <QtyComp
                    qty={item.holdings.quantity}
                    average_price={item.holdings.avg_price}
                  />
                ) : null
              }
              bottomComp={
                <HoldingBottomComponent
                  current_value={currValue}
                  day_pnl={isExternalStock ? undefined : daypnlLive}
                  invested_amount={item.holdings.invested_amount}
                  overall_pnl={isExternalStock ? undefined : overall_pnl_live}
                  overall_pnl_perc={overall_pnl_perc_live}
                  day_pnl_perc={day_pnl_perc_live}
                  quantity={quantity}
                  prevClose={prevClose}
                />
              }
              handleClick={() => {
                if (item.metadata?.name && item.metadata.ind_key) {
                  void router.push(
                    generateDetailUrl({
                      stock_name: item.metadata.name,
                      company_code: item.metadata.ind_key,
                      link: item.metadata.relativepath,
                    }),
                  )
                }
              }}
            />
          )
        })}

      {!holdings?.length && (
        <div className="bg-brand-white shadow-one flex w-full flex-col items-center justify-center gap-2 rounded-xl p-5">
          <StencilCDNImage
            src="https://indcdn.indmoney.com/public/images/indstocksdashboardnostocksicon.png"
            width={50}
            height={50}
            alt="indstocks dashboard nostocks icon"
          />
          <p className="text-brand-grey-base text-center">
            You don't have any holdings
          </p>
        </div>
      )}
    </div>
  )
}
